/**
 * Route Metadata Registry — single source of truth for:
 * - Breadcrumb label
 * - Page title (document.title)
 * - Analytics route_id
 * - Shell context
 *
 * For nav items, metadata is derived from navConfig.
 * Additional entries cover detail/sub-routes not in the sidebar.
 *
 * TRP-414 — i18n RENDER-TIME RESOLUTION
 * Prior to TRP-414, breadcrumb labels and pageTitle strings were resolved
 * at module-import time and frozen into the registry Map. That blocked
 * locale switches from updating breadcrumbs/title without a route reload.
 *
 * Post-TRP-414, the registry stores i18n KEYS (not resolved English
 * strings). Resolution happens at render time via `safeShellT(t, ...)`,
 * so language switches reactively update breadcrumbs + document.title.
 *
 * Backward-compat: `breadcrumbLabel`/`pageTitle` (resolved English) remain
 * available on the returned metadata for any caller that has not adopted
 * the i18n path. `breadcrumbLabelKey`/`pageTitleKey` are the new fields.
 */

import { navItems, type NavShell } from './nav';
import type { TerminologySet } from './terminology';
import { safeShellT, type SafeShellTFunction } from '../i18n/safeShellT';

export interface RouteMetadata {
  /**
   * Human-readable breadcrumb label (English fallback).
   * BACKWARD-COMPAT — render-time consumers should prefer
   * `breadcrumbLabelKey` resolved via safeShellT.
   */
  breadcrumbLabel: string;
  /**
   * i18n key for the breadcrumb label (TRP-414).
   * Optional — when absent, callers should use `breadcrumbLabel` directly.
   */
  breadcrumbLabelKey?: string;
  /**
   * Page title suffix (prepended with "AMuster - "). English fallback.
   * BACKWARD-COMPAT — see breadcrumbLabel.
   */
  pageTitle: string;
  /**
   * i18n key for the page title (TRP-414).
   * Optional — when absent, callers should use `pageTitle` directly.
   */
  pageTitleKey?: string;
  /** Stable analytics route ID */
  route_id: string;
  /** Shell context */
  shell: NavShell;
}

// Build base registry from navConfig.
// EN strings come from nav.ts `label` field; keys come from nav.ts `labelKey`.
const registry = new Map<string, RouteMetadata>();
for (const item of navItems) {
  registry.set(item.path, {
    breadcrumbLabel: item.label,
    breadcrumbLabelKey: item.labelKey,
    pageTitle: item.label,
    pageTitleKey: item.labelKey,
    route_id: item.route_id,
    shell: item.shell,
  });
}

// ── Additional routes (detail pages, sub-routes not in sidebar) ─────────────

const additionalRoutes: Record<string, RouteMetadata> = {
  // Muster (EMS)
  '/ems/muster/history': {
    breadcrumbLabel: 'History',
    breadcrumbLabelKey: 'shell.route.additional.musterHistory.breadcrumb',
    pageTitle: 'Muster History',
    pageTitleKey: 'shell.route.additional.musterHistory.title',
    route_id: 'ops.ems.muster.history',
    shell: 'operations',
  },
  // Event Spine — neutral intake (A2)
  '/events/report': {
    breadcrumbLabel: 'Report Event',
    breadcrumbLabelKey: 'shell.route.additional.eventReport.breadcrumb',
    pageTitle: 'Report Event or Concern',
    pageTitleKey: 'shell.route.additional.eventReport.title',
    route_id: 'ops.events.report',
    shell: 'operations',
  },
  // Admin CMS
  '/admin/cms/entries': {
    breadcrumbLabel: 'Entries',
    breadcrumbLabelKey: 'shell.route.additional.cmsEntries.breadcrumb',
    pageTitle: 'CMS Entries',
    pageTitleKey: 'shell.route.additional.cmsEntries.title',
    route_id: 'admin.cms.entries',
    shell: 'admin',
  },
  // User
  '/user/profile': {
    breadcrumbLabel: 'Profile',
    breadcrumbLabelKey: 'shell.route.additional.userProfile',
    pageTitle: 'Profile',
    pageTitleKey: 'shell.route.additional.userProfile',
    route_id: 'ops.user.profile',
    shell: 'operations',
  },
  '/user/preferences': {
    breadcrumbLabel: 'Preferences',
    breadcrumbLabelKey: 'shell.route.additional.userPreferences',
    pageTitle: 'Preferences',
    pageTitleKey: 'shell.route.additional.userPreferences',
    route_id: 'ops.user.preferences',
    shell: 'operations',
  },
  // Settings sub-pages
  '/settings/branding': {
    breadcrumbLabel: 'Branding',
    breadcrumbLabelKey: 'shell.route.additional.brandingSetting',
    pageTitle: 'Branding',
    pageTitleKey: 'shell.route.additional.brandingSetting',
    route_id: 'ops.settings.branding',
    shell: 'operations',
  },
  '/settings/api-keys': {
    breadcrumbLabel: 'API Keys',
    breadcrumbLabelKey: 'shell.route.additional.apiKeys.breadcrumb',
    pageTitle: 'API Keys',
    pageTitleKey: 'shell.route.additional.apiKeys.title',
    route_id: 'ops.settings.api_keys',
    shell: 'operations',
  },
  // Billing sub-pages
  '/billing/invoices': {
    breadcrumbLabel: 'Invoices',
    breadcrumbLabelKey: 'shell.route.additional.invoices',
    pageTitle: 'Invoices',
    pageTitleKey: 'shell.route.additional.invoices',
    route_id: 'ops.billing.invoices',
    shell: 'operations',
  },
  '/billing/upgrade': {
    breadcrumbLabel: 'Upgrade',
    breadcrumbLabelKey: 'shell.route.additional.upgrade.breadcrumb',
    pageTitle: 'Upgrade Plan',
    pageTitleKey: 'shell.route.additional.upgrade.title',
    route_id: 'ops.billing.upgrade',
    shell: 'operations',
  },
  // NOTE: `/workflow/definitions/:id` is deliberately NOT registered here.
  // `registry` does exact-path lookups, so a token like `:id` would never
  // match a runtime path like `/workflow/definitions/<uuid>`. A pattern-
  // matcher would broaden scope beyond 7-Q; the landed UUID heuristic in
  // `buildBreadcrumbs` already renders the UUID segment as "Detail",
  // which is consistent with other detail pages in this repo (e.g.
  // `/assets/registry/:id` also has no entry here).
};

for (const [path, meta] of Object.entries(additionalRoutes)) {
  registry.set(path, meta);
}

// ── Segment label fallback (for dynamic segments like :id) ──────────────────
//
// TRP-414: stores i18n KEYS, not resolved English strings. Render-time
// resolution happens in `getSegmentLabel`. Each value is the canonical
// `shell.route.segment.<segment>` key for the corresponding segment.

const segmentLabelKeys: Record<string, string> = {
  dashboard: 'shell.route.segment.dashboard',
  admin: 'shell.route.segment.admin',
  hub: 'shell.route.segment.hub',
  muster: 'shell.route.segment.muster',
  messages: 'shell.route.segment.messages',
  clinic: 'shell.route.segment.clinic',
  hse: 'shell.route.segment.hse',
  training: 'shell.route.segment.training',
  inspection: 'shell.route.segment.inspection',
  incident: 'shell.route.segment.incident',
  events: 'shell.route.segment.events',
  report: 'shell.route.segment.report',
  user: 'shell.route.segment.user',
  settings: 'shell.route.segment.settings',
  billing: 'shell.route.segment.billing',
  // TRP-414-PATCH-A1: Added so `/compliance/...` breadcrumbs localize the
  // intermediate `compliance` segment under AR (resolves to the operator-
  // approved AR value used by `shell.nav.group.compliance`). Without this
  // entry the segment falls through to the capitalization heuristic and
  // renders "Compliance" under both EN and AR.
  compliance: 'shell.route.segment.compliance',
  campuses: 'shell.route.segment.campuses',
  buildings: 'shell.route.segment.buildings',
  zones: 'shell.route.segment.zones',
  users: 'shell.route.segment.users',
  students: 'shell.route.segment.students',
  classes: 'shell.route.segment.classes',
  visitors: 'shell.route.segment.visitors',
  cms: 'shell.route.segment.cms',
  roles: 'shell.route.segment.roles',
  profile: 'shell.route.segment.profile',
  preferences: 'shell.route.segment.preferences',
  branding: 'shell.route.segment.branding',
  'api-keys': 'shell.route.segment.api-keys',
  certificates: 'shell.route.segment.certificates',
  templates: 'shell.route.segment.templates',
  calendar: 'shell.route.segment.calendar',
  sessions: 'shell.route.segment.sessions',
  assessments: 'shell.route.segment.assessments',
  ojt: 'shell.route.segment.ojt',
  kpis: 'shell.route.segment.kpis',
  initiatives: 'shell.route.segment.initiatives',
  requests: 'shell.route.segment.requests',
  roi: 'shell.route.segment.roi',
  lrs: 'shell.route.segment.lrs',
  enrollments: 'shell.route.segment.enrollments',
  'learning-paths': 'shell.route.segment.learning-paths',
  courses: 'shell.route.segment.courses',
  catalog: 'shell.route.segment.catalog',
  'near-misses': 'shell.route.segment.near-misses',
  'corrective-actions': 'shell.route.segment.corrective-actions',
  investigations: 'shell.route.segment.investigations',
  'lessons-learned': 'shell.route.segment.lessons-learned',
  'safety-bulletins': 'shell.route.segment.safety-bulletins',
  regulatory: 'shell.route.segment.regulatory',
  taxonomy: 'shell.route.segment.taxonomy',
  'non-conformities': 'shell.route.segment.non-conformities',
  equipment: 'shell.route.segment.equipment',
  encounters: 'shell.route.segment.encounters',
  visits: 'shell.route.segment.visits',
  medications: 'shell.route.segment.medications',
  inventory: 'shell.route.segment.inventory',
  waste: 'shell.route.segment.waste',
  reports: 'shell.route.segment.reports',
  staff: 'shell.route.segment.staff',
  'auto-muster': 'shell.route.segment.auto-muster',
  schedule: 'shell.route.segment.schedule',
  list: 'shell.route.segment.list',
  history: 'shell.route.segment.history',
  new: 'shell.route.segment.new',
  edit: 'shell.route.segment.edit',
  entries: 'shell.route.segment.entries',
  types: 'shell.route.segment.types',
  'zone-types': 'shell.route.segment.zone-types',
  gates: 'shell.route.segment.gates',
  'scanner-devices': 'shell.route.segment.scanner-devices',
  'access-simulator': 'shell.route.segment.access-simulator',
  approvals: 'shell.route.segment.approvals',
  'access-reviews': 'shell.route.segment.access-reviews',
  invoices: 'shell.route.segment.invoices',
  upgrade: 'shell.route.segment.upgrade',
  workflow: 'shell.route.segment.workflow',
  definitions: 'shell.route.segment.definitions',
  versions: 'shell.route.segment.versions',
  canvas: 'shell.route.segment.canvas',
  instances: 'shell.route.segment.instances',
  // NOTE: a top-level `approvals` segment label already exists above
  // (Access Console usage). The /workflow/approvals route inherits it via
  // the shared segment lookup — no duplicate entry needed.
};

/**
 * English-fallback segment labels (TRP-414).
 *
 * Kept in lock-step with `segmentLabelKeys` so `safeShellT` can fall back
 * to a sensible English string when the active locale lacks the key OR
 * `t` is not supplied. Mirrors the prior `segmentLabels` const verbatim.
 */
const segmentLabelFallbacksEn: Record<string, string> = {
  dashboard: 'Dashboard',
  admin: 'Admin Console',
  hub: 'Hub',
  muster: 'Muster',
  messages: 'Messages',
  clinic: 'Clinic',
  hse: 'HSE',
  training: 'Training',
  inspection: 'Inspections',
  incident: 'Incidents',
  events: 'Events',
  report: 'Report',
  user: 'Account',
  settings: 'Settings',
  billing: 'Billing',
  // TRP-414-PATCH-A1 (see segmentLabelKeys above)
  compliance: 'Compliance',
  campuses: 'Campuses',
  buildings: 'Buildings',
  zones: 'Zones',
  users: 'Users',
  students: 'Students',
  classes: 'Classes',
  visitors: 'Visitors',
  cms: 'CMS',
  roles: 'Roles & Access',
  profile: 'Profile',
  preferences: 'Preferences',
  branding: 'Branding',
  'api-keys': 'API Keys',
  certificates: 'Certificates',
  templates: 'Templates',
  calendar: 'Calendar',
  sessions: 'Sessions',
  assessments: 'Assessments',
  ojt: 'OJT',
  kpis: 'KPIs',
  initiatives: 'Initiatives',
  requests: 'Requests',
  roi: 'ROI',
  lrs: 'LRS',
  enrollments: 'Enrollments',
  'learning-paths': 'Learning Paths',
  courses: 'Courses',
  catalog: 'Catalog',
  'near-misses': 'Near Misses',
  'corrective-actions': 'Corrective Actions',
  investigations: 'Investigations',
  'lessons-learned': 'Lessons Learned',
  'safety-bulletins': 'Safety Bulletins',
  regulatory: 'Regulatory',
  taxonomy: 'Taxonomy',
  'non-conformities': 'Non-Conformities',
  equipment: 'Equipment',
  encounters: 'Encounters',
  visits: 'Visits',
  medications: 'Medications',
  inventory: 'Inventory',
  waste: 'Waste Log',
  reports: 'Reports',
  staff: 'Staff',
  'auto-muster': 'Auto-Muster',
  schedule: 'Schedule',
  list: 'Completed',
  history: 'History',
  new: 'New',
  edit: 'Edit',
  entries: 'Entries',
  types: 'Entry Types',
  'zone-types': 'Zone Types',
  gates: 'Gates',
  'scanner-devices': 'Scanner Devices',
  'access-simulator': 'Simulator',
  approvals: 'Approvals',
  'access-reviews': 'Access Reviews',
  invoices: 'Invoices',
  upgrade: 'Upgrade',
  workflow: 'Workflow',
  definitions: 'Definitions',
  versions: 'Versions',
  canvas: 'Canvas',
  instances: 'Instances',
};

/**
 * Exported for tests + sanity checks. The two maps MUST have identical
 * key sets — guarded by nav-key-coverage.test.ts.
 */
export { segmentLabelKeys, segmentLabelFallbacksEn };

// ── Public API ──────────────────────────────────────────────────────────────

/** Get metadata for an exact route path */
export function getRouteMetadata(path: string): RouteMetadata | undefined {
  return registry.get(path);
}

/**
 * Get breadcrumb label for a path segment, with optional terminology override
 * and optional i18n resolution.
 *
 * Precedence (TRP-414):
 *   1. Terminology overlay (e.g. `campuses` → `locationLabelPlural`)
 *   2. i18n via safeShellT (when both `t` AND a known segment key)
 *   3. English fallback string from `segmentLabelFallbacksEn`
 *   4. Capitalized segment as last resort (unknown segment)
 */
export function getSegmentLabel(
  segment: string,
  terminology?: TerminologySet,
  t?: SafeShellTFunction,
): string {
  // 1. Terminology overlay (per-tenant; highest precedence)
  if (segment === 'campuses' && terminology) return terminology.locationLabelPlural;

  const fallbackEn = segmentLabelFallbacksEn[segment]
    || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');

  // 2 + 3. i18n with English fallback
  const key = segmentLabelKeys[segment];
  if (t && key) {
    return safeShellT(t, key, { fallback: fallbackEn });
  }

  // 4. English fallback / capitalized unknown
  return fallbackEn;
}

/** Determine shell from pathname */
export function getShellFromPath(pathname: string): NavShell {
  return pathname.startsWith('/admin') ? 'admin' : 'operations';
}

/**
 * Build breadcrumbs for a pathname.
 *
 * TRP-414: accepts optional `t` for render-time i18n resolution. When `t`
 * is supplied, breadcrumb labels are translated via safeShellT at call
 * time (not at registry-build time). Locale switches that re-invoke this
 * function will return freshly-translated labels — no route reload needed.
 *
 * Backward-compat: when `t` is not supplied, the resolved label is the
 * English fallback from the registry (`breadcrumbLabel`) or the segment
 * fallback. This preserves pre-TRP-414 behavior for any caller that has
 * not adopted the i18n path.
 */
export function buildBreadcrumbs(
  pathname: string,
  terminology?: TerminologySet,
  t?: SafeShellTFunction,
): Array<{ label: string; path: string }> {
  const segments = pathname.split('/').filter(Boolean);
  const crumbs: Array<{ label: string; path: string }> = [];
  let accumulated = '';

  for (const seg of segments) {
    accumulated += `/${seg}`;
    // Check registry first for exact path match
    const meta = registry.get(accumulated);
    let label: string;

    if (meta) {
      const fallbackEn = meta.breadcrumbLabel;
      label = t && meta.breadcrumbLabelKey
        ? safeShellT(t, meta.breadcrumbLabelKey, { fallback: fallbackEn })
        : fallbackEn;
    } else {
      label = getSegmentLabel(seg, terminology, t);
    }

    // For admin shell, replace "admin" segment with the localized "Admin Console"
    if (seg === 'admin') {
      const adminFallback = 'Admin Console';
      const adminLabel = t
        ? safeShellT(t, 'shell.adminConsole.breadcrumb', { fallback: adminFallback })
        : adminFallback;
      crumbs.push({ label: adminLabel, path: accumulated });
    } else {
      // Skip UUID-like segments in breadcrumbs display but include path
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}/.test(seg);
      const detailFallback = 'Detail';
      const detailLabel = t
        ? safeShellT(t, 'shell.route.detail', { fallback: detailFallback })
        : detailFallback;
      crumbs.push({ label: isUuid ? detailLabel : label, path: accumulated });
    }
  }

  return crumbs;
}
